import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// The publisher Windows names for our installers: the organisation Microsoft validated
// for Azure Artifact Signing. It is what we tell people to check before they click Run
// anyway, so if the signing identity ever changes, this has to change with it. (#839)
export const WINDOWS_PUBLISHER = 'Cadekami Software Solutions LLC';

// A drawing of the SmartScreen dialog rather than a screenshot: it shows our real
// publisher and file name, stays sharp at any size, and borrows nobody's image. It is
// hidden from screen readers because the step beside it says the same thing, and its
// "buttons" are spans so the keyboard never lands on a control that does nothing.
// With no known file name the App line is left out rather than guessed: the drawing is
// what people compare their screen against.
const SmartScreenWindow: React.FC<{ expanded: boolean; app: string | null }> = ({ expanded, app }) => (
  <div className="dl-ss-window" aria-hidden="true">
    <span className="dl-ss-window-close">&#x2715;</span>
    <p className="dl-ss-window-title">Windows protected your PC</p>
    <p className="dl-ss-window-body">
      Microsoft Defender SmartScreen prevented an unrecognized app from starting. Running this
      app might put your PC at risk.
    </p>
    {expanded ? (
      <dl className="dl-ss-window-details">
        {app && <><dt>App:</dt><dd>{app}</dd></>}
        <dt>Publisher:</dt>
        <dd><span className="dl-ss-check">{WINDOWS_PUBLISHER}</span></dd>
      </dl>
    ) : (
      <span className="dl-ss-window-link dl-ss-click">More info</span>
    )}
    <div className="dl-ss-window-buttons">
      {expanded && <span className="dl-ss-window-btn dl-ss-click">Run anyway</span>}
      <span className="dl-ss-window-btn">Don&rsquo;t run</span>
    </div>
  </div>
);

interface Props {
  // The file Windows names on the App line: the installer's own name, or Chaptery.exe
  // for the portable build, which is run from the unzipped folder. Null when unknown.
  app: string | null;
  portable: boolean;
  onClose: () => void;
  // The download button they pressed. Focus cannot be read back from the page: the
  // button is disabled while the link is fetched, and a browser drops focus from a
  // disabled element, so by the time this opens nothing is focused.
  returnFocusTo?: HTMLElement | null;
}

/**
 * Shown after a Windows download starts. Windows warns about new apps until enough
 * people have installed them, even signed ones, and "Don't run" is the big button, so
 * a new writer is likely to stop there. This says what they will see and how to carry
 * on, and why, in plain terms.
 */
const SmartScreenHelp: React.FC<Props> = ({ app, portable, onClose, returnFocusTo }) => {
  const dialog = useRef<HTMLDivElement>(null);
  // Whether the current click began on the backdrop. A drag that starts inside, say to
  // select the publisher name, and is let go outside still fires a click on the
  // backdrop, and must not close the instructions mid-read.
  const pressedBackdrop = useRef(false);

  // Focus goes to the dialog itself, not to Got it: a screen reader then announces its
  // title, and on a short screen Enter cannot dismiss it unread from a button scrolled
  // out of sight. Back to the button they pressed on close. The page behind stays put.
  useEffect(() => {
    const previous = returnFocusTo ?? (document.activeElement as HTMLElement | null);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
    // Only the element at the moment it opened matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="dl-ss-overlay"
      onMouseDown={e => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onClick={e => { if (pressedBackdrop.current && e.target === e.currentTarget) onClose(); }}
    >
      {/* tabIndex -1: clicking its text keeps focus in here rather than on the page, so
          Escape and the Tab loop still work afterwards. */}
      <div
        ref={dialog} tabIndex={-1}
        className="dl-ss-dialog" role="dialog" aria-modal="true"
        aria-labelledby="dl-ss-title" aria-describedby="dl-ss-intro"
        onKeyDown={e => {
          if (e.key === 'Escape') onClose();
          if (e.key === 'Tab') {
            const focusable = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('button, a[href]'));
            const at = focusable.indexOf(document.activeElement as HTMLElement);
            // From the dialog itself (at -1) the loop starts at either end.
            if (e.shiftKey && at <= 0) { e.preventDefault(); focusable[focusable.length - 1]?.focus(); }
            else if (!e.shiftKey && (at === -1 || at === focusable.length - 1)) { e.preventDefault(); focusable[0]?.focus(); }
          }
        }}
      >
        <div className="dl-ss-head">
          <h2 id="dl-ss-title">Windows may ask you to double-check</h2>
          <button className="acct-icon-btn" aria-label="Close" onClick={onClose}><X size={18} /></button>
        </div>

        <p id="dl-ss-intro" className="dl-ss-intro">
          Your download has started. When you open {portable ? <><strong>Chaptery.exe</strong> from the unzipped folder</> : 'it'},
          Windows may show a blue box that says <strong>Windows protected your PC</strong>. That is
          normal for a new app, and two clicks get you past it.
        </p>

        <ol className="dl-ss-steps">
          <li className="dl-ss-step">
            <p className="dl-ss-step-text">
              <span className="dl-ss-n" aria-hidden="true">1</span>
              <span>Click <strong>More info</strong>.</span>
            </p>
            <SmartScreenWindow expanded={false} app={app} />
          </li>
          <li className="dl-ss-step">
            <p className="dl-ss-step-text">
              <span className="dl-ss-n" aria-hidden="true">2</span>
              <span>
                Check that the publisher is <strong>{WINDOWS_PUBLISHER}</strong>, then
                click <strong>Run anyway</strong>.
              </span>
            </p>
            <SmartScreenWindow expanded app={app} />
          </li>
        </ol>

        <section className="dl-ss-why" aria-labelledby="dl-ss-why-title">
          <h3 id="dl-ss-why-title">Why does Windows do this?</h3>
          <p>
            Windows keeps a track record for every app. When an app is new, not many people have
            installed it yet, so Windows asks you to double-check before it runs. It does this even
            for apps that are properly signed, and it stops on its own once enough people have
            installed the app safely.
          </p>
          <p>
            Chaptery is new, and it is digitally signed: Microsoft has verified that it comes from
            our company, {WINDOWS_PUBLISHER}. That is the name Windows shows as the publisher.
          </p>
        </section>

        <p className="dl-ss-safety">
          If the publisher shows any other name, click <strong>Don&rsquo;t run</strong> and email{' '}
          <a href="mailto:support@trychaptery.com">support@trychaptery.com</a>.
        </p>

        <div className="dl-ss-actions">
          <button className="btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
};

export default SmartScreenHelp;
